import { z } from "zod";
import { IdSchema, IsoDateTimeSchema } from "./primitives.js";
/**
 * Where a chart entry came from when staff applied it from a patient's pre-visit questionnaire
 * (PA-6 / EXECDEV-214).
 *
 * The patient profile read adds `intakeProvenance` to the allergy, medication and past medical
 * history items applied that way, and to no other item. The profile read item schemas are
 * passthrough, so the key arrives without touching the byte-pinned patient-profile.ts (adding it
 * there would need a model re-approval), and no ChartSourceKind is added: the item keeps
 * `sourceKind: "patient_reported"`. Clinical-web shows one chip, "Patient intake · <date>", in
 * place of (never beside) the "Patient-reported" chip.
 *
 * The key is a read key only. The profile write schemas are strict, so editors copy items field
 * by field, and the database drops it from any snapshot item sent back to the profile writer.
 * It never reaches patient_pmh_json, the visit context or any model input.
 */
export const PATIENT_INTAKE_PROVENANCE_KEY = "intakeProvenance";
export const PatientIntakeProvenanceSchema = z.object({
    via: z.literal("patient_intake"),
    /** The clinic-local calendar date the patient submitted the questionnaire. */
    submittedOn: z.iso.date(),
});
/** A profile allergy, medication or history item's extra key, for callers that need it typed. */
export const PatientIntakeEntryExtrasSchema = z
    .object({ [PATIENT_INTAKE_PROVENANCE_KEY]: PatientIntakeProvenanceSchema.optional() })
    .passthrough();
/** The item's intake provenance, if it was applied from a questionnaire. */
export function patientIntakeProvenanceOf(item) {
    if (typeof item !== "object" || item === null)
        return undefined;
    const parsed = PatientIntakeProvenanceSchema.safeParse(item[PATIENT_INTAKE_PROVENANCE_KEY]);
    return parsed.success ? parsed.data : undefined;
}
/** Whether the item was applied from a patient's questionnaire. */
export function isPatientIntakeEntry(item) {
    return patientIntakeProvenanceOf(item) !== undefined;
}
/**
 * The immutable `source` an intake-applied chart row stores: the profile writer's own keys
 * (kind, recordedBy, idempotencyKey) with `via` and the submission it came from. Server-side
 * only; documented here so the SQL, the tests and the read projection agree. It carries no
 * questionnaire text.
 */
export const PatientIntakeChartSourceSchema = z
    .object({
    kind: z.literal("patient_reported"),
    via: z.literal("patient_intake"),
    recordedBy: IdSchema,
    idempotencyKey: z.string().min(1).max(128),
    intakeSubmissionId: IdSchema,
    appointmentId: IdSchema,
    intakeSubmittedAt: IsoDateTimeSchema,
})
    .strict();
//# sourceMappingURL=patient-intake-provenance.js.map